import React, { useEffect, useRef, useState } from 'react';
import FilterDropdown from '../Filter/components/FilterDropdown';
import { translate } from '../../services/translate';
import { useAuth } from '../../context/Auth';
import { centralAPI } from '../../services/central-api';
import { appAgentService } from '../../services/central-api/app-agent';
import { StyleProp, TextStyle, ViewStyle } from 'react-native';

type SetStateCallback<S> = ((prevState: S) => S);

interface CityPropertyItem {
    label: string;
    value: number;
    latitude: string;
    longitude: string;
}

interface Props {
    value: number;
    setValue: React.Dispatch<SetStateCallback<number>>;
    zIndex?: number;
    disabled?: boolean;
    labelFontSize?: number;
    label?: string;
    actingBodyId: number;
    setPin?: (pos: LatLng) => void;
    setCurrentAddress?: (value: string) => void;
    includeEmptyValue?: boolean;
    pin?: LatLng;
    proximityThresholdMeters?: number;
    labelStyle?: StyleProp<TextStyle>;
    viewStyle?: StyleProp<ViewStyle>;
    required?: boolean;
    hasNearLocation?: (hasValue: boolean) => void;
}

function toRad(deg: number) {
    return (deg * Math.PI) / 180;
}

function distanceInMeters(a: LatLng, b: LatLng) {
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);

    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

function findNearestCityProperty(items: CityPropertyItem[], pin: LatLng, thresholdMeters: number) {
    let nearest: CityPropertyItem | undefined;
    let nearestDistance = Infinity;

    for (const item of items) {
        if (!item.value) {
            continue;
        }
        const lat = Number(item.latitude);
        const lng = Number(item.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            continue;
        }
        const distance = distanceInMeters(pin, { lat, lng });
        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = item;
        }
    }

    return nearestDistance <= thresholdMeters ? nearest : undefined;
}

export default function CityPropertyDropdown({
    value,
    setValue,
    zIndex,
    disabled,
    labelFontSize,
    label,
    actingBodyId,
    setPin,
    setCurrentAddress,
    includeEmptyValue,
    pin,
    proximityThresholdMeters = 100,
    labelStyle,
    viewStyle,
    required,
    hasNearLocation
}: Props) {
    const [items, setItems] = useState<{ label: string; value: number; latitude: string; longitude: string; }[]>([]);
    const { user: authenticatedUser } = useAuth();

    const lastEmittedPinRef = useRef<LatLng | null>(null);

    const setPinRef = useRef(setPin);
    setPinRef.current = setPin;
    const setCurrentAddressRef = useRef(setCurrentAddress);
    setCurrentAddressRef.current = setCurrentAddress;
    const setValueRef = useRef(setValue);
    setValueRef.current = setValue;

    async function getCityProperties() {
        try {
            const actingBody = getActingBodyId();
            if (actingBody === 0) {
                setItems([]);
                return;
            }
            const response = await appAgentService.getDropDownCityProperties(actingBody);
            if (includeEmptyValue) {
                response.unshift({
                    code: '',
                    id: 0,
                    name: '',
                    type: '',
                    latitude: '0',
                    longitude: '0',
                });
            }
            setItems(response.map(cityProperty => ({
                label: `${cityProperty.code ? cityProperty.code + ' - ' : ''}${cityProperty.type ? cityProperty.type + ' - ' : ''}${cityProperty.name}`,
                value: cityProperty.id,
                latitude: cityProperty.latitude,
                longitude: cityProperty.longitude,
            })));
        } catch (err) {
            console.error(err);
        }
    }

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_occurrences) {
            return actingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    useEffect(() => {
        if (!authenticatedUser) {
            return;
        }
        getCityProperties();
    }, [actingBodyId, authenticatedUser]);

    async function loadCurrentAddress(pin: LatLng) {
        try {
            if (!setCurrentAddressRef.current) {
                return;
            }
            const currentAddress = await centralAPI.getAddressByPlace({
                latitude: pin.lat,
                longitude: pin.lng,
            });
            setCurrentAddressRef.current(currentAddress);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (!value || !setPinRef.current) {
            return;
        }
        const row = items.find(cp => cp.value === value);
        if (!row) {
            return;
        }
        const newPin = { lat: Number(row.latitude), lng: Number(row.longitude) };
        lastEmittedPinRef.current = newPin;
        setPinRef.current(newPin);
        loadCurrentAddress(newPin);
    }, [value, items]);

    useEffect(() => {
        if (!pin || !items.length) {
            return;
        }

        const last = lastEmittedPinRef.current;
        if (last && last.lat === pin.lat && last.lng === pin.lng) {
            return;
        }

        const nearest = findNearestCityProperty(items, pin, proximityThresholdMeters);
        hasNearLocation?.(!!nearest && nearest.value !== value);
        if (nearest && nearest.value !== value) {
            setValueRef.current(() => nearest.value);
        } else {
            setValueRef.current(() => 0);
        }
    }, [pin, items]);


    return (
        <FilterDropdown
            label={label ?? translate('cityProperty')}
            items={items}
            multiple={false}
            setValue={setValue}
            value={value}
            zIndex={zIndex}
            disabled={disabled}
            labelFontSize={labelFontSize}
            labelStyle={labelStyle}
            viewStyle={viewStyle}
            required={required}
        />
    );
}
